/* From AREP-UI */
import merge from 'deepmerge'
import type { AxiosStatic } from 'axios'
import type { ApiFactories } from '@/api'

export function generateHash(): string {
  return Math.random()
    .toString(16)
    .substring(2, 16)
}

export function isValueInPixel(value: string) {
  return /^(\d)+px$/.test(value)
}

export function isArFontSize(value: string) {
  return ['xl', 'lg', 'md', 'sm', 'xs', 'xxs'].indexOf(value) > -1
    || isValueInPixel(value)
}

export function isHexColorCode(code: string): boolean {
  return /^#([A-Fa-f0-9]{3}){1,2}$/.test(code)
}

export function hexToRGBA(hex, opacity) {
  let c
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = '0x' + c.join('')
    return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${opacity === undefined ? 1 : opacity})`
  }
  else {
    return null
  }
}

export const overwriteMerge = (destinationArray, sourceArray, options) => sourceArray

export function mergeObjects(objA, objB) {
  return merge(objA, objB, { arrayMerge: overwriteMerge })
}

export function convertToKebabCase(text: string) {
  if (typeof text !== 'string') return text
  return text.replace(/\s+/g, '-').toLowerCase()
}

/* From AM2 */

// moves an item in an array from one index to another.
// can also move with negative indexes, eg move to second last
// position (an index of -2).
export function moveInArray(arr: Array<any>, old_index: number, new_index: number): Array<any> {
  const arrCopy = [...arr]
  while (old_index < 0) {
    old_index += arrCopy.length
  }

  while (new_index < 0) {
    new_index += arrCopy.length
  }

  if (new_index >= arrCopy.length) {
    let k = new_index - arrCopy.length + 1
    while (k--) {
      arrCopy.push(undefined)
    }
  }

  arrCopy.splice(new_index, 0, arrCopy.splice(old_index, 1)[0])
  return arrCopy
};

export function generateDataTestPrettyName(text: string) {
  return (text || '').toLowerCase().replace(/\s+/g, '-').replace(/[^\da-z-]/gi, '')
}

/* From AM3 */

// TODO avoid using `any`
type Context = {
  $api: ApiFactories
  $arNotification: any
  $axios: AxiosStatic
  $router: any
  $arWorker: any
  $store: any
  $arUtils: any
  $arMediaQuery: any
  $updateArMediaQuery: any
  $arStyle: any
  $browser: any
}

// Replaces the 'this' keyword
export function getContext(): Context {
  // Handle server-side rendering where window is undefined
  if (typeof window === 'undefined') {
    return {
      $api: undefined,
      $arNotification: undefined,
      $axios: undefined,
      $router: undefined,
      $arWorker: undefined,
      $store: undefined,
      $arUtils: undefined,
      $arMediaQuery: {
        window: {
          maxWidth: () => false,
          minWidth: () => false,
        },
        sizeMap: {
          md: { min: 768 },
        },
      },
      $updateArMediaQuery: () => {},
      $arStyle: undefined,
      $browser: undefined,
    }
  }

  const context = window?.useNuxtApp?.()
  return {
    $api: context?.$api as ApiFactories,
    $arNotification: context?.$arNotification,
    $axios: context?.$axios,
    $router: context?.$router,
    $arWorker: context?.$arWorker,
    $store: context?.$store,
    $arUtils: context?.$arUtils,
    $arMediaQuery: context?.$arMediaQuery,
    $updateArMediaQuery: context?.$updateArMediaQuery,
    $arStyle: context?.$arStyle,
    $browser: context?.$browser,
  }
}
